import PropTypes from 'prop-types'
import Button from '@/shared/components/UI/Button'
import CheckCircleIcon from '@/shared/icons/CheckCircleIcon'
import CalendarIcon from '@/shared/icons/CalendarIcon'
import FiveStarReviewIcon from '@/components/Global/FiveStarReviewIcon'

const OPTIMIZATION_BENEFITS = [
  'We audit your setup against top-performing configurations',
  'Identify the gaps limiting your results',
  'Make improvements live during the call',
  'Build your roadmap to top-performer status',
]

// Quotes are excerpts from public reviews at https://www.trustpilot.com/review/dante-ai.com
const TESTIMONIALS = [
  {
    key: 'annick',
    quote:
      '"They make sense of the AI jungle. It\'s an user-friendly tool that simplifies AI implementation for us, as beginners and non IT\'ers. With good support and an easy to understand interface, it makes building agents easy."',
    attribution: '- Annick, Trustpilot',
  },
  {
    key: 'daniele',
    quote:
      '"Dante has been a consistently reliable tool for us. It\'s well built, easy to integrate into daily workflows, and delivers results that go beyond a basic chatbot experience."',
    attribution: '- Daniele, Trustpilot',
  },
]

const StepNoValue = ({ downgradeInsights, onBookSession, onLeave, isBookSessionLoading }) => {
  const stats = downgradeInsights?.stats
  const roiValue = stats?.roi_achieved?.value

  const statCards = [
    {
      key: 'resolved_customer_queries',
      value: stats?.resolved_customer_queries?.value ?? '0',
      label: stats?.resolved_customer_queries?.label || 'Resolved customer queries',
    },
    {
      key: 'hours_of_your_time_saved',
      value: stats?.hours_of_your_time_saved?.value ?? '0',
      label: stats?.hours_of_your_time_saved?.label || 'Hours of your time saved',
    },
    {
      key: 'cost_recovered',
      value: stats?.cost_recovered?.value ?? '0',
      label: stats?.cost_recovered?.label || 'Cost recovered',
    },
    ...(roiValue != null
      ? [
          {
            key: 'roi_achieved',
            value: roiValue,
            label: stats?.roi_achieved?.label || 'ROI achieved',
          },
        ]
      : []),
  ]

  return (
    <div className="flex flex-col gap-v3-md">
      {/* Stats grid */}
      <div className="flex flex-col gap-v3-xs">
        <p className="text-v3-black-secondary text-v3-h9 font-inter-variable">
          Here&apos;s what you&apos;ve achieved with Dante AI:
        </p>
        <div className="grid grid-cols-2 gap-v3-sm">
          {statCards.map((stat, index) => {
            const centerLastOfThree =
              statCards.length === 3 && index === statCards.length - 1
            return (
              <div
                key={stat.key}
                className={`rounded-v3-md border border-v3-card-border bg-white p-v3-md flex flex-col items-center gap-v3-xs ${
                  centerLastOfThree
                    ? 'col-span-2 justify-self-center w-[calc((100%-var(--v3-sm))/2)] max-w-full'
                    : ''
                }`}
              >
                <span className="text-v3-h4 font-bold text-v3-green-dark">{stat.value}</span>
                <span className="text-[13px] text-v3-black-secondary text-center">{stat.label}</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Reframe */}
      <p className="text-v3-black-secondary text-v3-h9 font-inter-variable italic">
        These results happened with your current setup. A 15-minute session with our team often doubles them.
      </p>

      {/* Optimization session card */}
      <div className="rounded-v3-lg border-2 border-v3-purple-primary bg-v3-purple-bg p-v3-md flex flex-col gap-v3-sm">
        <p className="text-[18px] font-bold text-v3-black-max">Free 15-min optimization session</p>
        <Button
          variant="primary"
          size="sm"
          label="Book my free session"
          icon={CalendarIcon}
          onClick={onBookSession}
          loading={isBookSessionLoading}
          loadingLabel="Opening calendar..."
        />
        <div className="flex flex-col gap-v3-xs">
          {OPTIMIZATION_BENEFITS.map((benefit) => (
            <div key={benefit} className="flex items-start gap-v3-xs">
              <CheckCircleIcon className="text-v3-green-dark w-5 h-5 flex-shrink-0 mt-[1px]" />
              <span className="text-v3-h9 text-v3-black-primary">{benefit}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Social proof */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-v3-sm">
        {TESTIMONIALS.map((t) => (
          <div
            key={t.key}
            className="rounded-v3-sm bg-white border border-v3-card-border p-v3-sm flex flex-col gap-v3-xs"
          >
            <FiveStarReviewIcon />
            <p className="text-v3-h10 italic text-v3-black-primary">{t.quote}</p>
            <p className="text-v3-h11 text-v3-black-secondary text-right mt-auto">{t.attribution}</p>
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-v3-xs w-full items-center">

        <Button
          variant="transparent"
          label="I'd rather explore other options"
          onClick={onLeave}
        />
      </div>
    </div>
  )
}

StepNoValue.propTypes = {
  downgradeInsights: PropTypes.shape({
    stats: PropTypes.shape({
      resolved_customer_queries: PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
      hours_of_your_time_saved: PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
      cost_recovered: PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
      roi_achieved: PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
    }),
    tier_downgrade_reason: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  }),
  onBookSession: PropTypes.func.isRequired,
  onLeave: PropTypes.func.isRequired,
  isBookSessionLoading: PropTypes.bool,
}

StepNoValue.defaultProps = {
  downgradeInsights: null,
  isBookSessionLoading: false,
}

export default StepNoValue
